/**
 * №44 — COMMAND RANKING, extracted so there is exactly ONE of it.
 *
 * This is Sprint 3's scoring, moved out of `use-command-index.ts` unchanged:
 * fuzzy match × recency boost × kind boost, sorted, capped. It lives on its own
 * because there are now two palettes — the console's (`CommandPalette.tsx`) and
 * the App Shell's (`routes/app/AppCommandPalette.tsx`) — and they must differ in
 * their CHROME and their COMMAND SET, never in how a command scores. Two shells
 * over one ranking; "why did it rank that differently over there" is not a
 * question this product should be able to raise.
 *
 * Nothing here touches React, the DOM or a route, so a `tsx` gate can score a
 * command set without a browser.
 */
import type { Command, CommandKind } from "./types";
import { recencyBoost, type RecencyMap } from "./commands/recent";

/** nav + recent rank above prompts; actions/system rank below nav (S3.16). */
export const KIND_BOOST: Record<CommandKind, number> = {
  recent: 1.2,
  nav: 1.15,
  create: 1.0,
  prompt: 0.95,
  action: 0.9,
  system: 0.8,
};

export const RESULT_LIMIT = 50;

/**
 * The ranked, filtered, capped result set for `query` — empty for a blank
 * query, which is what makes the empty state the RECENTS list rather than a
 * dump of every command in the workspace.
 */
export function rankCommands(query: string, all: Command[], recency: RecencyMap): Command[] {
  const q = query.trim();
  if (!q) return [];
  const scored: { cmd: Command; score: number }[] = [];
  for (const cmd of all) {
    const f = fuzzyScore(q, cmd.label, cmd.keywords);
    if (f <= 0) continue;
    // recencyBoost reads the clock in its own (non-render) scope.
    const score = f * recencyBoost(recency[cmd.id]) * KIND_BOOST[cmd.kind];
    scored.push({ cmd, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, RESULT_LIMIT).map((s) => s.cmd);
}

// ---------------------------------------------------------------------------
// Fuzzy scoring — substring/prefix/word-boundary first, subsequence fallback.
// Returns 0 for no match, up to 1 for an exact label hit.
// ---------------------------------------------------------------------------

export function fuzzyScore(query: string, label: string, keywords: string[] = []): number {
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  const lab = label.toLowerCase();

  if (lab === q) return 1;
  if (lab.startsWith(q)) return 0.95;
  if (lab.includes(q)) {
    const wordStart = lab.split(/[\s/._-]+/).some((w) => w.startsWith(q));
    return wordStart ? 0.9 : 0.8;
  }

  const kw = keywords.join(" ").toLowerCase();
  if (kw.includes(q)) return 0.55;

  const sub = subsequenceScore(q, lab);
  if (sub > 0) return 0.3 + sub * 0.2; // 0.3–0.5
  const subk = subsequenceScore(q, kw);
  if (subk > 0) return 0.2 + subk * 0.1; // 0.2–0.3
  return 0;
}

/** 0 if `q` is not an in-order subsequence of `text`; else 0.5–1 by contiguity. */
function subsequenceScore(q: string, text: string): number {
  if (!q || !text) return 0;
  let ti = 0;
  let matched = 0;
  let contiguous = 0;
  let maxContiguous = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const c = q[qi];
    let found = false;
    while (ti < text.length) {
      if (text[ti] === c) {
        found = true;
        ti++;
        matched++;
        contiguous++;
        maxContiguous = Math.max(maxContiguous, contiguous);
        break;
      }
      ti++;
      contiguous = 0;
    }
    if (!found) return 0;
  }
  if (matched < q.length) return 0;
  return 0.5 + 0.5 * (maxContiguous / q.length);
}
